import { jsx as _jsx } from "react/jsx-runtime";
import { useState } from 'react';
import { usePricingContext } from '../../hooks/usePricingContext';
import { useCartStore } from '../../store/cartStore';
import { track } from '../../utils/analytics';
import { TEST_IDS } from '../../utils/testIds';
import NotifySignup from './NotifySignup';
/**
 * The one Add-to-Cart button every product page uses. Derives its label + disabled state
 * from `usePricingContext`, pushes into the cart store, and fires an `add_to_cart` event.
 * Briefly flashes "Added" on success.
 *
 * For in-development products this short-circuits to a NotifySignup form — callers don't
 * need to branch, they just render <StockCTA /> regardless of lifecycle.
 */ export default function StockCTA({ product, quantity = 1, className, variant = 'primary' }) {
    const { ctaLabel, canAddToCart, displayPriceCents, isInDevelopment } = usePricingContext(product);
    const addItem = useCartStore((s)=>s.addItem);
    const [state, setState] = useState('idle');
    if (isInDevelopment) {
        return /*#__PURE__*/ _jsx(NotifySignup, {
            product: product,
            variant: "inline",
            tone: "dark",
            className: className
        });
    }
    const base = variant === 'primary' ? 'bg-stone-900 border-stone-900 text-white hover:bg-stone-700 hover:border-stone-700' : 'bg-transparent border-stone-900 text-stone-900 hover:bg-stone-900 hover:text-white';
    const addedStyle = 'bg-green-600 border-green-600 text-white';
    const handleClick = ()=>{
        if (!canAddToCart) return;
        addItem({
            productId: product.id,
            name: product.name,
            quantity,
            priceCents: Number(product.priceCents),
            imageUrl: product.imageUrl
        });
        track({
            type: 'add_to_cart',
            payload: {
                itemId: product.id,
                itemName: product.name,
                priceCents: displayPriceCents,
                slug: product.slug,
                quantity
            }
        });
        setState('added');
        window.setTimeout(()=>setState('idle'), 1000);
    };
    return /*#__PURE__*/ _jsx("button", {
        type: "button",
        "data-testid": TEST_IDS.pdpAddToCartButton,
        onClick: handleClick,
        disabled: !canAddToCart || state === 'added',
        className: [
            'w-full py-4 text-xs font-semibold uppercase tracking-[0.15em] border-2 transition-colors',
            state === 'added' ? addedStyle : base,
            !canAddToCart ? 'opacity-60 cursor-not-allowed' : '',
            className ?? ''
        ].filter(Boolean).join(' '),
        children: state === 'added' ? 'Added' : ctaLabel
    });
}
